import { Fragment, lazy, Suspense } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import AuthGuard from 'src/components/AuthGuard'
import GuestGuard from 'src/components/GuestGuard'
import LoadingScreen from 'src/components/LoadingScreen'
import { CARGO_URLS, ORDER_URLS } from './constants.js'

export const RenderRoutes = (routes = []) => (
  <Suspense fallback={<LoadingScreen />}>
    <Switch>
      {routes?.map((route, i) => {
        const Guard = route.guard || Fragment
        // const Layout = route.layout || Fragment
        const Component = route.component
        const componentProps = route.componentProps || {}

        return (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={(props) => (
              <Guard>
                {route.routes ? (
                  RenderRoutes(route.routes)
                ) : (
                  <Component {...{ ...props, ...componentProps }} />
                )}
              </Guard>
            )}
          />
        )
      })}
    </Switch>
  </Suspense>
)

const routes = [
  {
    exact: true,
    path: '/shipmenttag',
    component: lazy(() => import('./layouts/ShipmentTag/ShipmentTag.jsx')),
  },
  {
    exact: true,
    path: '/pdf/create',
    component: lazy(() => import('./layouts/ShipmentTag/PDFCreate/PDFCreate')),
  },
  {
    exact: true,
    path: '/404',
    component: lazy(() => import('src/views/errors/NotFoundView')),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/login',
    component: lazy(() => import('src/views/auth/LoginView')),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/verify-otp',
    component: lazy(() => import('src/views/auth/VerifyOtp')),
  },
  {
    exact: true,
    path: '/login-unprotected',
    component: lazy(() => import('src/views/auth/LoginView')),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/register',
    component: lazy(() => import('src/views/auth/RegisterView')),
  },
  {
    exact: true,
    path: '/register-unprotected',
    component: lazy(() => import('src/views/auth/RegisterView')),
  },
  {
    exact: true,
    path: '/dashboard',
    component: () => <Redirect to="/app/reports/dashboard" />,
  },
  {
    path: '/app',
    guard: AuthGuard,
    // layout: DashboardLayout,
    routes: [
      {
        exact: true,
        path: '/app/account',
        component: lazy(() => import('src/views/account/AccountView')),
      },
      {
        exact: true,
        path: '/app/customs/customs-rf',
        component: lazy(() =>
          import('src/views/app/on-customs/CustomsView/CustomsRFView')
        ),
      },
      {
        exact: true,
        path: '/app/distribution/distribution-rf',
        component: lazy(() =>
          import('src/views/app/distribution/DistributionRFView')
        ),
      },
      {
        exact: true,
        path: `/app/orders/${ORDER_URLS.NEW_ORDERS}`,
        component: lazy(() => import('src/views/app/orders/OrderListView')),
        componentProps: {
          statusId: 10,
        },
      },
      {
        exact: true,
        path: `/app/orders/${ORDER_URLS.WAITING_TO_BE_PURCHASED}`,
        component: lazy(() => import('src/views/app/orders/OrderListView')),
        componentProps: {
          statusId: 50,
        },
      },
      {
        exact: true,
        path: `/app/orders/${ORDER_URLS.REDEEMED}`,
        component: lazy(() => import('src/views/app/orders/OrderListView')),
        componentProps: {
          statusId: 40,
        },
      },
      {
        exact: true,
        path: `/app/orders/${ORDER_URLS.GOODS_MORE_THEN_FIVE_DAY}`,
        component: lazy(() => import('src/views/app/orders/orderMoreThenFive')),
        componentProps: {
          statusId: 60,
        },
      },
      {
        exact: true,
        path: `/app/orders/${ORDER_URLS.PENDING_PRICE_CONFIRMATION}`,
        component: lazy(() => import('src/views/app/orders/OrderListView')),
        componentProps: {
          statusId: 20,
        },
      },
      {
        exact: true,
        path: `/app/orders/${ORDER_URLS.MOVED_TO_GOODS}`,
        component: lazy(() => import('src/views/app/orders/OrderListView')),
        componentProps: {
          statusId: 60,
        },
      },
      {
        exact: true,
        path: `/app/orders/${ORDER_URLS.CANCELED}`,
        component: lazy(() => import('src/views/app/orders/OrderListView')),
        componentProps: {
          statusId: 30,
        },
      },
      {
        exact: true,
        path: `/app/orders/${ORDER_URLS.WAITING_FOR_PAYMENT_ON_DOBROPOIZON}`,
        component: lazy(() => import('src/views/app/orders/OrderListView')),
        componentProps: {
          statusId: 70,
        },
      },
      {
        exact: true,
        path: '/app/cargo/courier-accept',
        component: lazy(() =>
          import('src/views/app/cargo/CargoAcceptFromCurier')
        ),
      },
      {
        exact: true,
        path: '/app/cargo/courier-accept/mobile',
        component: lazy(() =>
          import('src/views/app/cargo/cargoAcceptFromCourierMobile')
        ),
      },
      {
        exact: true,
        path: '/app/cargo/courier-accept/mobile-redesign',
        component: lazy(() =>
          import('src/views/app/cargo/cargoAcceptFromCourierMobileRedesign')
        ),
      },
      {
        exact: true,
        path: '/app/cargo/accept',
        component: lazy(() => import('src/views/app/cargo/Accept')),
      },
      {
        exact: true,
        path: '/app/cargo/create-parcel',
        component: lazy(() => import('src/views/app/cargo/CreateParcel')),
      },
      {
        exact: true,
        path: '/app/cargo/create-parcel/see/:id',
        component: lazy(() =>
          import('src/views/app/cargo/CreateParcel/OrderDetailsView')
        ),
      },
      {
        exact: true,
        path: `/app/cargo/returns/${CARGO_URLS.CARGO_RETURNS_BY_CODE}`,
        component: lazy(() => import('src/views/app/cargo/Returns')),
        componentProps: {
          searchType: 1,
        },
      },
      {
        exact: true,
        path: `/app/cargo/returns/${CARGO_URLS.CARGO_RETURNS_BY_ADDRESS}`,
        component: lazy(() => import('src/views/app/cargo/Returns')),
        componentProps: {
          searchType: 2,
        },
      },
      // {
      //   exact: true,
      //   path: '/app/cargo/edit',
      //   component: lazy(() => import('src/views/app/cargo/CargoAddView')),
      //   componentProps: {
      //     mode: 'edit'
      //   }
      // },
      {
        exact: true,
        path: '/app/cargo/create',
        component: lazy(() => import('src/views/app/cargo/CargoAddView')),
      },
      {
        exact: true,
        path: '/app/orders/:orderId',
        component: lazy(() => import('src/views/app/orders/OrderDetailsView')),
      },
      {
        exact: true,
        path: '/app/parcels/complectation',
        component: lazy(() =>
          import('src/views/app/parcels/complectation/ParcelListViewV2')
        ),
      },
      {
        exact: true,
        path: '/app/parcels/complectation/:id',
        component: lazy(() =>
          import('src/views/app/parcels/complectation/CargoList')
        ),
      },
      {
        exact: true,
        path: '/app/parcel/assemble/:id/:clientId',
        component: lazy(() => import('src/views/app/cargo/ComplectParcel')),
      },
      {
        exact: true,
        path: '/app/parcels/send',
        component: lazy(() =>
          import('src/views/app/parcels/send/ParcelListViewV2')
        ),
      },
      {
        exact: true,
        path: '/app/parcels/change-parcel-data',
        component: lazy(() =>
          import('src/views/app/parcels/parcelsChangeData')
        ),
      },
      {
        exact: true,
        path: '/app/parcels/send/details/:id',
        component: lazy(() =>
          import('src/views/app/parcels/send/OrderDetailsView')
        ),
      },
      {
        exact: true,
        path: '/app/parcels/pay',
        component: lazy(() =>
          import('src/views/app/parcels/pay/ParcelListViewV2')
        ),
      },
      {
        exact: true,
        path: '/app/parcels/bags',
        component: lazy(() => import('src/views/app/parcels/bags')),
      },
      {
        exact: true,
        path: '/app/parcels/bags/:id',
        component: lazy(() => import('src/views/app/parcels/bags/BagItemView')),
      },
      {
        exact: true,
        path: '/app/parcels/verification',
        component: lazy(() =>
          import('src/views/app/parcels/PaymentVerification')
        ),
      },
      {
        exact: true,
        path: '/app/parcels/pay/details/:id',
        component: lazy(() =>
          import('src/views/app/parcels/pay/OrderDetailsView')
        ),
      },
      {
        exact: true,
        path: `/app/parcels/${ORDER_URLS.CHECKING_REGISTRY_DATA_CHECK}`,
        component: lazy(() =>
          import('src/views/app/checkingRegistry/checking')
        ),
        componentProps: {
          statusId: 60,
        },
      },
      {
        exact: true,
        path: `/app/parcels/${ORDER_URLS.CHECKING_REGISTRY_DATA}`,
        component: lazy(() => import('src/views/app/checkingRegistry/table')),
        componentProps: {
          statusId: 60,
        },
      },
      {
        exact: true,
        path: `/app/parcels/${ORDER_URLS.CHECKING_REGISTRY_DATA_TABLE}`,
        component: lazy(() => import('src/views/app/checkingRegistry/table')),
        componentProps: {
          statusId: 60,
        },
      },
      {
        exact: true,
        path: `/app/parcels/${ORDER_URLS.CHECKING_REGISTRY_DATA_TABLE}/:id`,
        component: lazy(() =>
          import('src/views/app/checkingRegistry/table/edit')
        ),
      },
      {
        exact: true,
        path: `/app/parcels/shipments/${ORDER_URLS.CHECKING_REGISTRY_DATA_TABLE_SHIPMENT}`,
        component: lazy(() =>
          import('src/views/app/checkingRegistry/shipmentTable')
        ),
        componentProps: {
          statusId: 60,
        },
      },
      {
        exact: true,
        path: `/app/parcels/shipments/${ORDER_URLS.CHECKING_REGISTRY_DATA_TABLE_SHIPMENT}/:id`,
        component: lazy(() =>
          import('src/views/app/checkingRegistry/shipmentTable/edit/index.jsx')
        ),
      },
      {
        exact: true,
        path: `/app/parcels/shipments/${ORDER_URLS.CHECKING_REGISTRY_DATA_SHIPMENT}`,
        component: lazy(() =>
          import('src/views/app/checkingRegistry/checkingShipment')
        ),
      },
      {
        exact: true,
        path: '/app/parcels/dispatch-register-view',
        component: lazy(() =>
          import(
            'src/views/app/reference/DispatchRegister/DispatchRegisterView'
          )
        ),
      },
      {
        exact: true,
        path: '/app/cargo/expected',
        component: lazy(() =>
          import('src/views/app/parcels/expected/ParcelListView')
        ),
      },
      {
        exact: true,
        path: '/app/cargo/expected/details/:id',
        component: lazy(() =>
          import('src/views/app/parcels/expected/OrderDetailsView')
        ),
      },
      // {
      //   exact: true,
      //   path: '/app/parcels/customs',
      //   component: lazy(() => import('src/views/app/parcels/customs/ParcelListView'))
      // },
      {
        exact: true,
        path: '/app/parcels/delivery/details/:id',
        component: lazy(() =>
          import('src/views/app/parcels/delivery/OrderDetailsView')
        ),
      },
      // {
      //   exact: true,
      //   path: '/app/parcels/delivery',
      //   component: lazy(() => import('src/views/app/parcels/delivery/ParcelListView'))
      // },
      {
        exact: true,
        path: '/app/parcels/customs/details/:id',
        component: lazy(() =>
          import('src/views/app/parcels/customs/OrderDetailsView')
        ),
      },
      {
        exact: true,
        path: '/app/parcels/weight',
        component: lazy(() =>
          import('src/views/app/parcels/weightning/ParcelListViewV2')
        ),
      },
      {
        exact: true,
        path: '/app/parcels/weight/details/:id',
        component: lazy(() =>
          import('src/views/app/parcels/weightning/OrderDetailsView')
        ),
      },
      {
        exact: true,
        path: '/app/parcels/:parcelId',
        component: lazy(() => import('src/views/app/parcels/Details')),
      },
      {
        exact: true,
        path: '/app/cargo-package/cargos-on-assembly',
        component: lazy(() =>
          import('src/views/app/cargo_package/cargo_package_onAssembly')
        ),
      },
      {
        exact: true,
        path: '/app/cargo-package/cargos-weigh-in',
        component: lazy(() =>
          import('src/views/app/cargo_package/cargo_package_weighIn')
        ),
      },
      {
        exact: true,
        path: '/app/cargo-package/cargos-for-shipment',
        component: lazy(() =>
          import('src/views/app/cargo_package/cargo_package_forShipment')
        ),
      },
      {
        exact: true,
        path: '/app/cargo-package/cargos-registry',
        component: lazy(() =>
          import('src/views/app/cargo_package/cargo_package_registry')
        ),
      },
      {
        exact: true,
        path: '/app/finances/rate',
        component: lazy(() =>
          import('src/views/app/finances/rate/RateListView')
        ),
      },
      {
        exact: true,
        path: '/app/finances/transactions',
        component: lazy(() =>
          import('src/views/app/finances/transactions/TransactionListView')
        ),
      },
      {
        exact: true,
        path: '/app/finances/accounts',
        component: lazy(() =>
          import('src/views/app/finances/accounts/AccountsListView')
        ),
      },
      {
        exact: true,
        path: '/app/tariffs/additional',
        component: lazy(() =>
          import('src/views/app/tariffs/AdditionalServicesView')
        ),
      },
      {
        exact: true,
        path: '/app/tariffs/tariff-groups',
        component: lazy(() => import('src/views/app/tariffs/TariffsGroups')),
      },
      {
        exact: true,
        path: '/app/tariffs/shipping',
        component: lazy(() => import('src/views/app/tariffs/ShippingTariffs')),
      },
      {
        exact: true,
        path: '/app/tariffs/last-mile',
        component: lazy(() => import('src/views/app/tariffs/LastMile')),
      },
      // DBP-693: merged into Потеряшки
      // {
      //   exact: true,
      //   path: '/app/reference/cargo-scan',
      //   component: lazy(() => import('src/views/app/reference/CargoScan')),
      // },
      {
        exact: true,
        path: '/app/reference/cargo-scan-device',
        component: lazy(() =>
          import('src/views/app/reference/CargoScanDevice')
        ),
      },
      {
        exact: true,
        path: '/app/reference/pvz-scan',
        component: lazy(() => import('src/views/app/reference/PvzScanLogs')),
      },
      // DBP-693: merged into Потеряшки
      // {
      //   exact: true,
      //   path: '/app/reference/cargo-scan',
      //   component: lazy(() => import('src/views/app/reference/CargoScan')),
      // },
      {
        exact: true,
        path: '/app/reference/register',
        component: lazy(() =>
          import('src/views/app/reference/Register/CargoList')
        ),
      },
      {
        exact: true,
        path: '/app/reference/tasks',
        component: lazy(() => import('src/views/app/reference/Tasks')),
      },
      {
        exact: true,
        path: '/app/dispose/tasks',
        component: lazy(() => import('src/views/app/reference/TasksDisposal')),
      },
      {
        exact: true,
        path: '/app/dispose/parcles',
        component: lazy(() =>
          import('src/views/app/reference/ParcelsDisposal')
        ),
      },
      {
        exact: true,
        path: '/app/dispatch-register/add-errors/:id',
        component: lazy(() =>
          import('src/views/app/reference/DispatchRegister/ErrorsPage')
        ),
      },
      {
        exact: true,
        path: '/app/dispatch-register/remove-errors/:id',
        component: lazy(() =>
          import('src/views/app/reference/DispatchRegister/ErrorsPage')
        ),
      },
      {
        exact: true,
        path: '/app/reference/orders',
        component: lazy(() => import('src/views/app/reference/Order')),
      },
      {
        exact: true,
        path: '/app/reference/cargo-categories',
        component: lazy(() =>
          import('src/views/app/reference/CargoCategories')
        ),
      },
      {
        exact: true,
        path: '/app/reference/danger-categories',
        component: lazy(() =>
          import('src/views/app/reference/DangerCategories')
        ),
      },
      {
        exact: true,
        path: '/app/reference/transport-type',
        component: lazy(() =>
          import('src/views/app/reference/TransportDelivery')
        ),
      },
      {
        exact: true,
        path: '/app/reference/country-settings',
        component: lazy(() =>
          import('src/views/app/reference/CountrySettings')
        ),
      },
      {
        exact: true,
        path: '/app/reference/warehouse-managment',
        component: lazy(() =>
          import('src/views/app/reference/WarehouseManagment')
        ),
      },
      {
        exact: true,
        path: '/app/reference/lost',
        component: lazy(() => import('src/views/app/reference/LostParcels')),
      },
      {
        exact: true,
        path: '/app/reference/parcels',
        component: lazy(() =>
          import('src/views/app/reference/Parcels/ParcelListView')
        ),
      },
      {
        exact: true,
        path: '/app/reference/parcels/:id',
        component: lazy(() =>
          import('src/views/app/reference/Parcels/OrderDetailsView')
        ),
      },
      {
        exact: true,
        path: '/app/reference/cargo/',
        component: lazy(() =>
          import('src/views/app/reference/Cargo/CargoList')
        ),
      },
      {
        exact: true,
        path: '/app/reference/cargo/:id',
        component: lazy(() => import('src/views/app/reference/Cargo/Details')),
      },
      {
        exact: true,
        path: '/app/reference/staff',
        component: lazy(() =>
          import('src/views/app/reference/Staff/StaffListView')
        ),
      },

      {
        exact: true,
        path: '/app/reference/cargo-package-all',
        component: lazy(() =>
          import('src/views/app/cargo_package/all_cargo_packages')
        ),
      },
      {
        exact: true,
        path: '/app/reference/shipments',
        component: lazy(() => import('src/views/app/shipments')),
      },
      {
        exact: true,
        path: '/app/reference/staff/edit/:id',
        component: lazy(() =>
          import('src/views/app/reference/Staff/StaffEditView')
        ),
        componentProps: {
          mode: 'edit',
        },
      },
      {
        exact: true,
        path: '/app/reference/staff/add',
        component: lazy(() =>
          import('src/views/app/reference/Staff/StaffEditView')
        ),
      },
      {
        exact: true,
        path: '/app/reference/addresses',
        component: lazy(() =>
          import('src/views/app/reference/Addresses/AddressesListView')
        ),
      },
      {
        exact: true,
        path: '/app/reference/clients',
        component: lazy(() =>
          import('src/views/app/reference/Clients/ClientsListView')
        ),
      },
      {
        exact: true,
        path: '/app/reference/clients/edit/:id',
        component: lazy(() =>
          import('src/views/app/reference/Clients/ClientsEditView')
        ),
        componentProps: {
          mode: 'edit',
        },
      },
      {
        exact: true,
        path: '/app/reference/clients/add',
        component: lazy(() =>
          import('src/views/app/reference/Clients/ClientsEditView')
        ),
      },
      {
        exact: true,
        path: '/app/reference/passport-verification',
        component: lazy(() => import('src/views/app/reference/Passports')),
      },
      {
        exact: true,
        path: '/app/reference/passports-with-partner',
        component: lazy(() =>
          import('src/views/app/reference/Passports/PassportsShipments.js')
        ),
      },
      // {
      //   exact: true,
      //   path: '/app/tariffs/zones',
      //   component: lazy(() => import('src/views/app/tariffs/Zones'))
      // },
      {
        exact: true,
        path: '/app/management/products',
        component: lazy(() => import('src/views/product/ProductListView')),
      },
      {
        exact: true,
        path: '/app/management/products/create',
        component: lazy(() => import('src/views/product/ProductCreateView')),
      },
      {
        exact: true,
        path: '/app/management',
        component: () => <Redirect to="/app/management/customers" />,
      },
      {
        exact: true,
        path: '/app/projects/overview',
        component: lazy(() => import('src/views/project/OverviewView')),
      },
      {
        exact: true,
        path: '/app/projects/browse',
        component: lazy(() => import('src/views/project/ProjectBrowseView')),
      },
      {
        exact: true,
        path: '/app/projects/create',
        component: lazy(() => import('src/views/project/ProjectCreateView')),
      },
      {
        exact: true,
        path: '/app/projects/:id',
        component: lazy(() => import('src/views/project/ProjectDetailsView')),
      },
      {
        exact: true,
        path: '/app/projects',
        component: () => <Redirect to="/app/projects/browse" />,
      },
      {
        exact: true,
        path: '/app/reports/dashboard',
        component: lazy(() => import('src/views/reports/DashboardView')),
      },
      {
        exact: true,
        path: '/app/reports',
        component: () => <Redirect to="/app/reports/dashboard" />,
      },
      {
        exact: true,
        path: '/app/social/feed',
        component: lazy(() => import('src/views/social/FeedView')),
      },
      {
        exact: true,
        path: '/app/social/profile',
        component: lazy(() => import('src/views/social/ProfileView')),
      },
      {
        exact: true,
        path: '/app/social',
        component: () => <Redirect to="/app/social/profile" />,
      },
      {
        exact: true,
        path: '/app/pvz/accept',
        component: lazy(() => import('src/views/app/pvz/Accept')),
      },
      {
        exact: true,
        path: '/app/pvz/issue',
        component: lazy(() => import('src/views/app/pvz/Issue')),
      },
      {
        exact: true,
        path: '/app/pvz/parcel-history',
        component: lazy(() => import('src/views/app/pvz/ParcelHistory')),
      },
      {
        exact: true,
        path: '/app/pvz/balance',
        component: lazy(() => import('src/views/app/pvz/Balance')),
      },
      {
        exact: true,
        path: '/app/pvz',
        component: lazy(() => import('src/views/app/pvz')),
      },
      {
        exact: true,
        path: '/app',
        component: () => <Redirect to="/app/reports/dashboard" />,
      },
      {
        component: () => <Redirect to="/404" />,
      },
    ],
  },
  {
    path: '/',
    exact: true,
    component: () => <Redirect to="/app" />,
  },

  // {
  //   path: '*',
  //   layout: MainLayout,
  //   routes: [
  //     {
  //       exact: true,
  //       path: '/',
  //       component: HomeView
  //     },
  //     {
  //       exact: true,
  //       path: '/pricing',
  //       component: lazy(() => import('src/views/pricing/PricingView'))
  //     },
  //     {
  //       component: () => <Redirect to="/404" />
  //     }
  //   ]
  // // }
]

export default routes
